
import { Link } from 'react-router-dom';

const ServicesSection = () => {
  const services = [
    {
      title: "Safety Staffing",
      description: "When business needs require that you have a full-time, on-site safety professional for any reason, look no further. HP Inc. can provide a pre-qualified on-site safety manager through our safety staffing service for a variety of industries including construction, manufacturing, oil &amp; gas, power, and nuclear clients. Learn more about our experienced safety staffing professionals.",
      icon: "ri-team-fill",
      link: "/safety-staffing",
      color: "orange",
      featured: true,
      image: "https://public.readdy.ai/ai/img_res/c26129f0994dc9b424307987b6c1a8ea.jpg"
    },
    {
      title: "Industrial Hygiene Services",
      description: "Comprehensive environmental monitoring and occupational health services to protect your workforce. Our industrial hygiene specialists conduct air quality assessments, noise monitoring, chemical exposure evaluations, and develop control strategies to maintain a healthy work environment across all industries.",
      icon: "ri-microscope-fill",
      link: "/industrial-hygiene",
      color: "blue",
      featured: true,
      image: "https://readdy.ai/api/search-image?query=Industrial%20hygienist%20in%20protective%20equipment%20conducting%20air%20quality%20monitoring%20with%20professional%20testing%20equipment%2C%20industrial%20facility%20background%2C%20environmental%20health%20assessment%2C%20occupational%20safety%20specialist%20with%20monitoring%20devices&width=300&height=300&seq=industrial-hygiene-1&orientation=squarish"
    },
    {
      title: "OSHA Consulting",
      description: "No matter your company size, HP Inc. has the resources to help your company meet the challenges of the ever-changing business landscape. As OSHA regulations are updated, it is important that your company incorporates those changes into its safety program. Learn more about how our team can help you meet your safety goals.",
      icon: "ri-shield-check-fill",
      link: "/osha-consulting",
      color: "green",
      featured: false,
      image: "https://readdy.ai/api/search-image?query=OSHA%20safety%20consultant%20reviewing%20compliance%20documents%20and%20safety%20protocols%20at%20industrial%20workplace%2C%20professional%20in%20business%20attire%20with%20hard%20hat%2C%20safety%20audit%20and%20inspection%20process&width=300&height=300&seq=osha-consulting-1&orientation=squarish"
    },
    {
      title: "OSHA Training",
      description: "Safety Training is an essential part of any robust safety program. HP Inc. offers instructor-led safety training on-site or at our training center. We also offer computer-based OSHA safety training courses when you require additional flexibility. Check out our current list of open enrollment safety training classes.",
      icon: "ri-graduation-cap-fill",
      link: "/osha-training",
      color: "purple",
      featured: false,
      image: "https://readdy.ai/api/search-image?query=OSHA%20safety%20training%20session%20with%20instructor%20teaching%20workers%20in%20classroom%20setting%2C%20safety%20equipment%20displays%2C%20professional%20training%20environment%2C%20students%20in%20hard%20hats%20taking%20notes&width=300&height=300&seq=osha-training-1&orientation=squarish"
    }
  ];

  const getColorClasses = (color: string, featured: boolean) => {
    const baseClasses = featured ? 'ring-2 ring-offset-2' : '';
    switch (color) {
      case 'orange':
        return `${baseClasses} ${featured ? 'ring-orange-500 border-orange-300' : 'border-orange-200'} hover:border-orange-400`;
      case 'blue':
        return `${baseClasses} ${featured ? 'ring-blue-500 border-blue-300' : 'border-blue-200'} hover:border-blue-400`;
      case 'green':
        return `${baseClasses} ${featured ? 'ring-green-500 border-green-300' : 'border-green-200'} hover:border-green-400`;
      case 'purple':
        return `${baseClasses} ${featured ? 'ring-purple-500 border-purple-300' : 'border-purple-200'} hover:border-purple-400`;
      default:
        return `${baseClasses} border-gray-200 hover:border-gray-400`;
    }
  };

  const getIconColor = (color: string) => {
    switch (color) {
      case 'orange': return 'text-orange-500';
      case 'blue': return 'text-blue-500';
      case 'green': return 'text-green-500';
      case 'purple': return 'text-purple-500';
      default: return 'text-gray-500';
    }
  };

  const getButtonColor = (color: string) => {
    switch (color) {
      case 'orange': return 'bg-orange-500 hover:bg-orange-600';
      case 'blue': return 'bg-blue-500 hover:bg-blue-600';
      case 'green': return 'bg-green-500 hover:bg-green-600';
      case 'purple': return 'bg-purple-500 hover:bg-purple-600';
      default: return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Core Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            HP Inc Consultant Services specializes in Safety Staffing and Industrial Hygiene services, providing comprehensive solutions for heavy civil and industrial projects.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div key={index} className="group">
              <Link to={service.link} className="block">
                <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border ${getColorClasses(service.color, service.featured)} ${service.featured ? 'transform hover:scale-105' : ''}`}>
                  <div className="relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-48 object-cover"
                    />
                    {service.featured && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                          FEATURED
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4">
                      <div className={`w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg`}>
                        <i className={`${service.icon} text-2xl ${getIconColor(service.color)}`}></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-orange-600 transition-colors">
                      {service.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-sm mb-4">
                      {service.description}
                    </p>
                    <button className={`w-full text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 ${getButtonColor(service.color)}`}>
                      Learn More
                    </button>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border border-orange-200">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Need Immediate Safety Support?</h3>
            <p className="text-gray-600 mb-6">
              Contact HP Inc Consultant Services today for emergency safety staffing or urgent industrial hygiene assessments. Our team is ready to deploy qualified professionals to your site within 24-48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/safety-staffing" className="safety-button">
                Request Safety Staffing
              </Link>
              <Link to="/industrial-hygiene" className="safety-button-secondary">
                Schedule Hygiene Assessment
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
